import {createAsyncThunk} from '@reduxjs/toolkit'

import {isNull} from '@shared/helpers/lang'
import {pickBy} from '@shared/helpers/object'

import {changeLocation} from '@web/actions/application'
import {L_PAY, PAY_M} from '@web/core/urls'
import {Sentry} from '@web/helpers/log'
import {postLog} from '@web/helpers/nelo'
import {urlMergedWithUnverifiedUrlParam} from '@web/helpers/path'

import {NavigateToPaymentSdkFailType} from '../constants'
import {ThunkApi} from '../interfaces'

export type NavigationOption = {
    url: string
    resultCode: string
    [key: string]: string
}

// 허용된 targetOrigin 목록
const ALLOWED_ORIGINS_SET = new Set<string>([L_PAY, PAY_M])
const isValidOrigin = (value: string): boolean => {
    return ALLOWED_ORIGINS_SET.has(value)
}

/** 결제형  > sdk 호출 */
const navigateToPaymentSdk = createAsyncThunk<void, NavigationOption, ThunkApi<{type: NavigateToPaymentSdkFailType}>>(
    'navigateTo/PaymentSdk',
    async (options, {dispatch, rejectWithValue}) => {
        const {url, resultCode, targetOrigin, ...restOptions} = options
        const bodyParameter = pickBy((value) => !isNull(value) && value !== '', restOptions)
        const redirectUrl = urlMergedWithUnverifiedUrlParam(url, {...bodyParameter, resultCode})

        // 결제형 SDK 호환로직 템플릿
        const messageBody = {
            type: 'NSP_PAY_POPUP_RETURN',
            code: resultCode,
            redirectUrl,
            body: {...bodyParameter, returnUrl: url, resultCode},
        }

        // opener 내 function 직접 호출 가능한 구 브라우저 환경에서만 직접 호출
        try {
            if (!window.opener) {
                throw new Error('Not able to call Function in window opener')
            }

            if (!isValidOrigin(targetOrigin ?? '')) {
                throw new Error(
                    `Invalid target origin: ${targetOrigin} not included in ${JSON.stringify(
                        Array.from(ALLOWED_ORIGINS_SET),
                    )}`,
                )
            }

            /**
             * https://oss.fin.navercorp.com/paynsp/qa/issues/3254
             * Win + Whale/Firefox에서는 포커스가 오프너에게 이동하지 않는 이슈가 있음
             * 커스텀 얼럿으로 수정 후 삭제
             */
            window.opener.focus()
            postLog({code: 'NSP_PAY_POPUP_OPEN', level: 'INFO', message: JSON.stringify(messageBody)})

            window.opener.postMessage(JSON.stringify(messageBody), targetOrigin)

            /**
             * sdk에게 요청했지만 10000ms 후에도 안닫힌다면 창닫기 (로그용)
             * 안드로이드에서는 부모에게 focus가 사라지는 경우가 있음
             * https://oss.fin.navercorp.com/paynsp/qa/issues/3456
             */
            await new Promise<void>((_resolve) => {
                setTimeout(async () => {
                    try {
                        Sentry.sendGeneralError(new Error('결제 SDK 응답 없음'))
                        await Promise.race([
                            postLog({
                                code: 'NSP_PAY_SDK_NO_RESPONSE',
                                level: 'INFO',
                                message: JSON.stringify({...messageBody}),
                            }),
                            new Promise((resolve) => setTimeout(() => resolve('timeout'), 200)),
                        ])
                    } catch {
                        // 이 블록은 실행되지만, 로깅 실패가 창 닫기를 막아서는 안됨
                    } finally {
                        dispatch(changeLocation({type: 'CLOSE'}))
                    }
                }, 10000)
            })

            return undefined
        } catch (error) {
            postLog({
                code: 'NSP_PAY_SDK_FAIL',
                level: 'INFO',
                message: JSON.stringify({
                    ...messageBody,
                    error: String(error),
                }),
            })

            return rejectWithValue({type: 'NSP_PAY_SDK_FAIL'})
        }
    },
)

export default navigateToPaymentSdk
